import { useContext } from 'react';
import { useLocation } from 'react-router';
import { SELECTED_COMMERCIAL_PRODUCT_INDEX } from '../../../common/constants/commonConstants.js';
import { useSubmitFbbOrder } from '../../../common/hooks/useSubmitFbbOrder';
import { SiteContext } from '../../../public/site/SiteContext.js';
import { createNettiliteEcommerceItemsFromConfiguredOffer } from '../../../selfservice/common/googleEcommerceEvent.js';
import { FixedBroadbandBillingInformation } from '../FixedBroadbandBillingInformation.js';
import { createFixedBroadbandOrder } from '../FixedBroadbandSupportCaseUtils.js';
import { getAddedAddOns } from '../FixedBroadbandUtils.js';
import type { FixedBBCheckoutLoaderData } from '../../../common/loaders.js';
import type { ConfiguredOffer } from '../../../common/types/commercialProduct.js';
import type { PurposeOfUseOrContact } from '../../../common/types/subscription.js';
import type { AddressSearchMatch } from '../../../components/AddressSearch/AddressSearch.js';
import type { AddOn } from '../../../generated/api/addOn.js';
import type { BillingAccount } from '../../../generated/api/billingAccount.js';
import type { CampaignAssociation } from '../../../generated/api/campaignAssociation.js';
import type { CompanyInfoResponse } from '../../../generated/api/companyInfoResponse.js';
import type { OnlineModel } from '../../../generated/api/onlineModel.js';
import type { CheckoutStepProps } from '../FixedBroadbandCheckout.js';
import type { FixedBroadbandInstallationDetails } from '../FixedBroadbandSetupDate.js';

interface StepBillingInformationProps extends CheckoutStepProps {
  configuredOffer: ConfiguredOffer;
  fixedBroadbandOnlineModel: OnlineModel;
  selectedCampaign?: CampaignAssociation;
  setCampaignAutoSelect: React.Dispatch<React.SetStateAction<boolean>>;
  additionalAddOns: AddOn[];
  addressSearchMatch: AddressSearchMatch;
  purposeOfUseOrContact?: PurposeOfUseOrContact;
  yritystuplaPurposeOfUse?: PurposeOfUseOrContact;
  broadbandInstallationDetails?: FixedBroadbandInstallationDetails;
  companyInfo?: CompanyInfoResponse;
  fixedBBCheckoutLoaderData: FixedBBCheckoutLoaderData;
}

export const StepBillingInformation = (props: StepBillingInformationProps) => {
  const submitFbbOrder = useSubmitFbbOrder();
  const { siteBaseUrl } = useContext(SiteContext);
  const { pathname, search } = useLocation();

  const {
    configuredOffer,
    fixedBroadbandOnlineModel,
    selectedCampaign,
    setCampaignAutoSelect,
    additionalAddOns,
    addressSearchMatch,
    purposeOfUseOrContact,
    yritystuplaPurposeOfUse,
    broadbandInstallationDetails,
    companyInfo,
    fixedBBCheckoutLoaderData,
  } = props;

  const contacts = fixedBBCheckoutLoaderData.contacts.contacts || [];

  const configuredCommercialProduct = configuredOffer.selectedCommercialProducts[SELECTED_COMMERCIAL_PRODUCT_INDEX];
  const selectedCommercialProduct = configuredCommercialProduct.commercialProduct;

  const submitFixedBroadBandOrderCase = (existingBaDispIdOrNewBa: string | BillingAccount | undefined) => {
    configuredCommercialProduct.addedAddOns = [
      ...(configuredCommercialProduct.addedAddOns || []),
      ...getAddedAddOns(additionalAddOns),
    ];

    const existingBaDispId = typeof existingBaDispIdOrNewBa === 'string' ? existingBaDispIdOrNewBa : undefined;
    const newBa = typeof existingBaDispIdOrNewBa === 'object' ? existingBaDispIdOrNewBa : undefined;

    const fixedBroadbandOrder = createFixedBroadbandOrder({
      contacts,
      addressSearchMatch,
      selectedCommercialProduct,
      purposeOfUseOrContact: purposeOfUseOrContact!,
      yritystuplaPurposeOfUse,
      broadbandInstallationDetails,
      currentUrl: `${siteBaseUrl}${pathname}${search}`,
      companyInfo: companyInfo!,
      billingAccountDisplayId: existingBaDispId,
      configuredOffer,
    });

    const eCommerceEventProducts = createNettiliteEcommerceItemsFromConfiguredOffer(configuredOffer);

    void submitFbbOrder(fixedBroadbandOrder, configuredOffer, eCommerceEventProducts, newBa);
  };

  return (
    <FixedBroadbandBillingInformation
      onSubmit={submitFixedBroadBandOrderCase}
      configuredOffer={configuredOffer}
      fixedBroadBandOnlineModel={fixedBroadbandOnlineModel}
      selectedCampaign={selectedCampaign}
      setCampaignAutoSelect={setCampaignAutoSelect}
      billChannels={fixedBBCheckoutLoaderData.billChannels}
      billingAccounts={fixedBBCheckoutLoaderData.billingAccounts}
      companyInfo={companyInfo}
      contacts={contacts}
    />
  );
};
